import { GridIconActions, GridIconButton, PopupBody, PopupHeader, PopupWrapper } from '@/components'
import { ModalLayout } from '@/components/HeadlessUI'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { AnimationSkeleton } from '@/pages/ui/Skeleton'
import {
	normalizePaymentKind,
	normalizePaymentStatus,
	SubscriptionPaymentStatusTypes,
	TransactionPaymentKind,
	type TransactionDetailResponse,
} from '@/services/transactionTypes'
import type { ReactNode } from 'react'

const statusLabel = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'Succeeded'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'Failed'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'Pending'
		case SubscriptionPaymentStatusTypes.Refunded:
			return 'Refunded'
		default:
			return 'Unknown'
	}
}

const statusClass = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'orbit-pill orbit-pill--success'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'orbit-pill orbit-pill--danger'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'orbit-pill orbit-pill--warning'
		default:
			return 'orbit-pill'
	}
}

const statusIcon = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'ri-checkbox-circle-fill'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'ri-close-circle-fill'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'ri-time-fill'
		case SubscriptionPaymentStatusTypes.Refunded:
			return 'ri-refund-2-line'
		default:
			return 'ri-information-fill'
	}
}

const formatMoney = (amount: number, currency: string) => {
	try {
		return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount)
	} catch {
		return `${amount.toFixed(2)} ${currency.toUpperCase()}`
	}
}

const formatDate = (value: string) => {
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

const discountLabel = (type: string | number, value: number) => {
	const isPercent = type === 'Percentage' || type === 0
	return isPercent ? `${value}%` : formatMoney(value, 'USD')
}

const MetaRow = ({ icon, label, value }: { icon: string; label: string; value: ReactNode }) => (
	<div className="flex items-center gap-2 py-1.5">
		<i className={`${icon} w-4 shrink-0 text-sm text-[hsl(var(--primary))]`} aria-hidden />
		<span className="w-[5.5rem] shrink-0 text-[11px] font-medium text-[hsl(var(--muted-foreground))]">{label}</span>
		<div className="min-w-0 flex-1 text-sm font-medium text-[hsl(var(--foreground))] break-all">{value || '—'}</div>
	</div>
)

const PackStat = ({ icon, label, value }: { icon: string; label: string; value: number | null | undefined }) => (
	<div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.25)] px-2.5 py-2">
		<i className={`${icon} text-base text-[hsl(var(--primary))]`} aria-hidden />
		<div className="min-w-0">
			<p className="font-display text-base font-semibold leading-tight tabular-nums">{value ?? 0}</p>
			<p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{label}</p>
		</div>
	</div>
)

type Props = {
	detail: TransactionDetailResponse | null
	loading: boolean
	showTenant: boolean
	onClose: () => void
}

export const TransactionDetailModal = ({ detail, loading, showTenant, onClose }: Props) => {
	const kind = detail ? normalizePaymentKind(detail.kind) : TransactionPaymentKind.Subscription
	const isPack = kind === TransactionPaymentKind.Pack
	const promo = !isPack ? detail?.promoCode : null

	return (
		<ModalLayout isStatic showModal toggleModal={onClose} panelClassName={MODAL_PANEL_CLASS_MD} placement={MODAL_PLACEMENT_CENTER}>
			<PopupWrapper variant="default" className="!m-0 !shadow-none !border-0">
				<PopupHeader title="Transaction details" onClose={onClose} />
				<PopupBody className="!py-3 sm:!py-3">
					{loading || !detail ? (
						<AnimationSkeleton />
					) : (
						<div className="space-y-3">
							<div className="relative overflow-hidden rounded-xl border border-[hsl(var(--border))]">
								<div className="absolute inset-y-0 left-0 w-1 bg-[hsl(var(--primary))]" />
								<div className="flex flex-wrap items-center justify-between gap-3 py-3 pl-4 pr-3">
									<div className="min-w-0">
										<p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
											Amount charged
										</p>
										<p className="font-display mt-0.5 text-2xl font-semibold leading-tight tracking-tight text-[hsl(var(--foreground))]">
											{formatMoney(detail.amount, detail.currency)}
											<span className="ml-1.5 align-middle text-xs font-medium text-[hsl(var(--muted-foreground))]">
												{detail.currency?.toUpperCase()}
											</span>
										</p>
										<p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
											<i className="ri-calendar-event-line mr-1" aria-hidden />
											{formatDate(detail.createdOn)}
										</p>
									</div>
									<div className="flex flex-wrap items-center gap-1.5">
										<span className="orbit-pill orbit-pill--neutral inline-flex items-center gap-1 text-xs">
											<i className={isPack ? 'ri-box-3-line' : 'ri-calendar-check-line'} aria-hidden />
											{isPack ? 'Pack' : 'Subscription'}
										</span>
										<span className={`${statusClass(detail.paymentStatus)} inline-flex items-center gap-1`}>
											<i className={statusIcon(detail.paymentStatus)} aria-hidden />
											{statusLabel(detail.paymentStatus)}
										</span>
									</div>
								</div>
							</div>

							{promo && (
								<div className="overflow-hidden rounded-xl border border-dashed border-[hsl(var(--primary)/0.45)] bg-[hsl(var(--primary)/0.06)] px-3 py-2.5">
									<div className="flex flex-wrap items-center justify-between gap-2">
										<div className="min-w-0">
											<p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">Promo applied</p>
											<p className="font-display mt-0.5 text-lg font-semibold leading-tight tracking-wide text-[hsl(var(--foreground))]">
												{promo.code}
											</p>
											{promo.description && (
												<p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{promo.description}</p>
											)}
										</div>
										<span className="orbit-pill orbit-pill--success text-xs">
											−{discountLabel(promo.discountType, promo.discountValue)}
										</span>
									</div>
									<div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
										<div>
											<p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Original</p>
											<p className="mt-0.5 text-sm font-medium tabular-nums">{formatMoney(promo.originalAmount, detail.currency)}</p>
										</div>
										<div>
											<p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Saved</p>
											<p className="mt-0.5 text-sm font-semibold tabular-nums text-[hsl(var(--primary))]">
												−{formatMoney(promo.discountAmount, detail.currency)}
											</p>
										</div>
										<div>
											<p className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Final</p>
											<p className="mt-0.5 text-sm font-medium tabular-nums">{formatMoney(promo.finalAmount, detail.currency)}</p>
										</div>
									</div>
									<p className="mt-1.5 text-center text-[11px] text-[hsl(var(--muted-foreground))]">Used {formatDate(promo.usedOn)}</p>
								</div>
							)}

							<div className="rounded-xl border border-[hsl(var(--border))] px-3 py-1">
								{showTenant && (
									<>
										<MetaRow icon="ri-building-4-line" label="Tenant" value={detail.tenantName} />
										<MetaRow icon="ri-mail-line" label="Email" value={detail.tenantAdminEmail} />
									</>
								)}
								<MetaRow icon="ri-price-tag-3-line" label="Plan" value={detail.planName} />
								{isPack && <MetaRow icon="ri-box-3-line" label="Pack" value={detail.packName} />}
								<MetaRow
									icon="ri-bank-card-line"
									label="Stripe ID"
									value={detail.stripePaymentIntentId ? <span className="font-mono text-xs">{detail.stripePaymentIntentId}</span> : '—'}
								/>
								<MetaRow
									icon="ri-file-text-line"
									label="Invoice"
									value={
										detail.invoiceUrl ? (
											<a href={detail.invoiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline">
												Open invoice
												<i className="ri-external-link-line text-xs" aria-hidden />
											</a>
										) : (
											'—'
										)
									}
								/>
							</div>

							{isPack && (
								<div className="grid grid-cols-3 gap-2">
									<PackStat icon="ri-user-add-line" label="Users" value={detail.userCount} />
									<PackStat icon="ri-server-line" label="Instances" value={detail.instanceCount} />
									<PackStat icon="ri-database-2-line" label="Databases" value={detail.databaseCount} />
								</div>
							)}

							{!isPack && !promo && (
								<p className="flex items-center gap-2 rounded-lg border border-dashed border-[hsl(var(--border))] px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
									<i className="ri-coupon-3-line" aria-hidden />
									No promo code was used on this transaction.
								</p>
							)}
						</div>
					)}
				</PopupBody>
			</PopupWrapper>
		</ModalLayout>
	)
}

export const TransactionViewButton = ({ onClick }: { onClick: () => void }) => (
	<GridIconActions>
		<GridIconButton icon="ri-eye-line" title="View transaction" onClick={onClick} />
	</GridIconActions>
)

export default TransactionDetailModal
